import {
  telemetryName,
  type RendererTelemetryInput,
  type TelemetryFunnelStep,
} from '../shared/telemetry';
import type {
  TelemetryCatalogAttrs,
  TelemetryEventName,
  TelemetrySessionContext,
} from '../shared/telemetry-catalog';

/**
 * Product telemetry from the renderer (ADR-0081): screens, clicks by control
 * name, shortcuts, dialogs opened and abandoned, error banners and toasts, and
 * uncaught errors. A control's name comes only from the product's code: its
 * own or its nearest container's `data-telemetry`, otherwise its CSS class.
 * Never from what the control shows or says (its text, aria-label or title):
 * those name the customer's things (a job, a folder, a field, a document type,
 * a scanner) as often as the product's, and no rule can tell which. Nothing
 * typed into a field is ever read.
 */

const track = (input: RendererTelemetryInput) => {
  try {
    window.capturePointDesktop?.trackTelemetry?.(input);
  } catch {
    /* telemetry never breaks a screen */
  }
};

let lastScreen = '';
/** The screen or route now in front. Repeats are ignored. */
export function reportScreen(name: string): void {
  if (!name || name === lastScreen) return;
  lastScreen = name;
  track({ kind: 'screen', name });
}

export function reportFeature(name: string): void {
  track({ kind: 'feature', name });
}
export function reportFunnel(step: TelemetryFunnelStep): void {
  track({ kind: 'funnel', step });
}
export function reportTiming(name: string, durationMs: number): void {
  track({ kind: 'perf', name, durationMs });
}

/**
 * A named event from the closed catalog (shared/telemetry-catalog.ts), with only the attributes it declares. Main
 * checks it again and drops anything else. `jobId` says which job it happened in; it never leaves the computer.
 * Fire-and-forget: nothing waits on telemetry.
 */
export function reportEvent(
  name: TelemetryEventName,
  options: { attrs?: TelemetryCatalogAttrs; jobId?: string; durationMs?: number } = {},
): void {
  track({ kind: 'event', name, ...options });
}

/** What the main window knows as the session starts (the tour, hints), added to the session's start. Once a window. */
export function reportSessionContext(
  context: Pick<TelemetrySessionContext, 'tour' | 'hints'>,
): void {
  track({ kind: 'context', ...context });
}

/** A toast or banner the person saw. Main scrubs the text again. */
export function reportNotice(message: string, name = 'toast'): void {
  if (message) track({ kind: 'notice', name, message });
}

/**
 * A toast or banner that is a plain notice whatever its words say (the banner after the person's own Stop setup reads
 * "Setup stopped"): counted as `ui.toast` or `ui.alert`, never `ui.error-…`. Its words are not read at all.
 */
export function reportPlainNotice(name: 'toast' | 'alert'): void {
  track({ kind: 'notice', name, message: '' });
}

/** A container whose data-telemetry names the message it shows (`error:<code>`, or `notice`), not a control. */
const MESSAGE_NAME = '[data-telemetry^="error:"], [data-telemetry="notice"]';

/** The element's first class that reads as a code name (not a state such as is-open), else its tag. */
const classToken = (element: Element) => {
  const token = [...element.classList].find(
    (name) => /^[a-z][a-z0-9-]{2,40}$/.test(name) && !/^(is|has)-/.test(name),
  );
  return token ?? element.tagName.toLowerCase();
};

/**
 * A control's code name, or null when it has none safe to send: the nearest `data-telemetry`
 * (on the control or a container), else the control's class. Its text, aria-label and title are
 * never read (tests/telemetry-tests/no-user-text.cjs keeps it that way).
 */
export function controlName(element: Element): string | null {
  // A message's code (data-telemetry="error:<code>" or "notice") names the message, not the buttons inside it.
  const explicit = element
    .closest(`[data-telemetry]:not(${MESSAGE_NAME})`)
    ?.getAttribute('data-telemetry');
  if (explicit) return telemetryName(explicit);
  return telemetryName(classToken(element));
}

/**
 * A window's code name: its `data-telemetry-dialog` (every WorkspaceDialog names itself), else its data-telemetry or
 * class. `none` marks something that only looks like a window (a popover always in the page, the selected-text
 * menu): it is not counted, so it is never "left" either.
 */
const dialogName = (element: Element): string | null => {
  const own = element.getAttribute('data-telemetry-dialog');
  if (own !== null) return own === 'none' ? null : (telemetryName(own) ?? 'dialog');
  return telemetryName(element.getAttribute('data-telemetry') ?? classToken(element)) ?? 'dialog';
};

let quietClicks = 0;
/**
 * Clicks a control on the person's behalf without counting it as their click: the tour choosing a filter as a step
 * opens. `click()` dispatches at once, so the listener below sees the count raised. A click from the keyboard
 * (arrow keys moving between tabs) is the person's own and is still counted.
 */
export function clickWithoutTelemetry(element: HTMLElement): void {
  quietClicks += 1;
  try {
    element.click();
  } finally {
    quietClicks -= 1;
  }
}

/**
 * Whether a click inside a window finishes it (a dialog closed by it is complete, not abandoned). A window's main
 * button finishes it. A button with no type of its own (a window's X among them) reads as "submit" through `.type`,
 * so only one that says submit counts; closing with the X is leaving. A destructive question's confirm button is a
 * danger button, so it carries `confirm-action-confirm` to be counted.
 */
export function dialogClickCompletes(target: Pick<Element, 'getAttribute' | 'className'>): boolean {
  return (
    target.getAttribute('type') === 'submit' ||
    /primary|confirm|save|create|done|finish|apply/i.test(String(target.className))
  );
}

let installed = false;

/** Install the renderer's listeners once per window. */
// standards: allow function-length because CapturePoint port, split tracked in docs/engineering/workbench-port.md
export function installRendererTelemetry(windowLabel: string): () => void {
  if (installed) return () => undefined;
  installed = true;
  const cleanup: Array<() => void> = [];
  const on = <K extends keyof WindowEventMap>(
    type: K,
    listener: (event: WindowEventMap[K]) => void,
  ) => {
    window.addEventListener(type, listener, true);
    cleanup.push(() => window.removeEventListener(type, listener, true));
  };

  on('error', (event) => {
    if (!(event instanceof ErrorEvent)) return;
    track({
      kind: 'error',
      name: `renderer.uncaught`,
      message: event.message,
      detail: event.error instanceof Error ? (event.error.stack ?? '') : '',
    });
  });
  on('unhandledrejection', (event) => {
    const reason = event.reason;
    track({
      kind: 'error',
      name: 'renderer.unhandled-rejection',
      message: reason instanceof Error ? reason.message : String(reason),
      detail: reason instanceof Error ? (reason.stack ?? '') : '',
    });
  });

  // Clicks on controls, by name.
  let lastDialogClick: { dialog: Element; complete: boolean; at: number } | null = null;
  on('click', (event) => {
    if (quietClicks > 0) return;
    const target =
      event.target instanceof Element
        ? event.target.closest(
            'button, a, [role=button], [role=menuitem], [role=tab], [role=switch], input[type=checkbox], input[type=radio], select, summary',
          )
        : null;
    if (!target) return;
    const name = controlName(target);
    if (name) track({ kind: 'feature', name: `click:${name}`.slice(0, 120) });
    const dialog = target.closest('[role=dialog], [role=alertdialog]');
    if (dialog) {
      lastDialogClick = { dialog, complete: dialogClickCompletes(target), at: performance.now() };
    }
  });
  // Keyboard shortcuts, by key combination only. Typing into a field is never read.
  on('keydown', (event) => {
    const functionKey = /^F\d{1,2}$/.test(event.key);
    if (!event.ctrlKey && !event.altKey && !event.metaKey && !functionKey && event.key !== 'Escape')
      return;
    if (['Control', 'Alt', 'Shift', 'Meta'].includes(event.key)) return;
    const key = event.key.length === 1 ? event.key.toLowerCase() : event.key.toLowerCase();
    if (
      !/^[a-z0-9,.\/;\[\]=+-]$|^f\d{1,2}$|^(escape|enter|tab|arrow\w+|page\w+|home|end|delete|backspace|space)$/.test(
        key,
      )
    )
      return;
    // Punctuation a code name cannot hold is named (Ctrl+, opens Settings; it was sent as "ctrl+").
    const named =
      (
        {
          ',': 'comma',
          ';': 'semicolon',
          '[': 'bracketleft',
          ']': 'bracketright',
          '=': 'equal',
        } as Record<string, string>
      )[key] ?? key;
    const combo = [
      event.ctrlKey && 'ctrl',
      event.altKey && 'alt',
      event.shiftKey && 'shift',
      event.metaKey && 'meta',
      named,
    ]
      .filter(Boolean)
      .join('+');
    track({ kind: 'feature', name: `shortcut:${combo}` });
  });

  // Dialogs opened and closed, error banners shown, and the welcome screen.
  // Open windows by element, each with its own number, so two with one name are told apart in main.
  const openDialogs = new Map<Element, { name: string; key: number }>();
  let dialogKeys = 0;
  const seenAlerts = new WeakSet<Element>();
  const scan = (root: Element) => {
    const candidates = [
      root,
      ...root.querySelectorAll(
        '[role=dialog], [role=alertdialog], [role=alert], [role=status][data-telemetry^="error:"], .job-library-empty',
      ),
    ];
    for (const element of candidates) {
      if (!(element instanceof Element)) continue;
      const role = element.getAttribute('role');
      if ((role === 'dialog' || role === 'alertdialog') && !openDialogs.has(element)) {
        const name = dialogName(element);
        if (name === null) continue;
        const key = ++dialogKeys;
        openDialogs.set(element, { name, key });
        track({ kind: 'dialog', name, state: 'open', key });
      } else if ((role === 'alert' || role === 'status') && !seenAlerts.has(element)) {
        seenAlerts.add(element);
        // A message the person saw carries its code (data-telemetry="error:<code>"): that is sent, never its words.
        // A banner marked "notice" is a plain alert whatever it says, and its words are not read. Any other banner
        // is counted as an alert, as before; a status line without a code is not read.
        const named = element.getAttribute('data-telemetry') ?? '';
        const coded = /^error:([a-z0-9-]{1,60})$/.exec(named);
        if (coded)
          track({
            kind: 'event',
            name: `ui.error:${coded[1]}`,
            attrs: { kind: role === 'alert' ? 'alert' : 'status' },
          });
        else if (named === 'notice') {
          if (role === 'alert') reportPlainNotice('alert');
        } else if (role === 'alert') {
          const text = (element.textContent ?? '').trim();
          if (text) track({ kind: 'notice', name: 'alert', message: text.slice(0, 300) });
        }
      } else if (element.classList.contains('job-library-empty')) {
        track({ kind: 'funnel', step: 'welcome' });
      }
    }
  };
  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) if (node instanceof Element) scan(node);
      if (mutation.removedNodes.length === 0) continue;
      for (const [element, { name, key }] of openDialogs) {
        if (element.isConnected) continue;
        openDialogs.delete(element);
        const click = lastDialogClick;
        // A window that closes by itself once its work is done says so (the tour's reading window at "Your tour is
        // ready"): that is a completed dialog, not one the person left.
        const complete =
          element.hasAttribute('data-telemetry-complete') ||
          (click !== null &&
            click.dialog === element &&
            click.complete &&
            performance.now() - click.at < 3_000);
        track({ kind: 'dialog', name, state: complete ? 'complete' : 'close', key });
      }
    }
  });
  const start = () => {
    if (!document.body) return;
    scan(document.body);
    observer.observe(document.body, { childList: true, subtree: true });
  };
  if (document.body) start();
  else window.addEventListener('DOMContentLoaded', start, { once: true });
  cleanup.push(() => observer.disconnect());
  track({ kind: 'feature', name: `window:${windowLabel}` });
  return () => {
    for (const stop of cleanup) stop();
    installed = false;
  };
}
